import React from 'react';
import { useLanguage } from '../context/LanguageContext';

const content = {
  sv: {
    title: "Integritetspolicy",
    lastUpdated: "Senast uppdaterad: Mars 2026",
    sections: [
      {
        heading: "1. Inledning",
        text: "Quran Online (quranonline.se) drivs av Ustadh Ismail. Vi respekterar din integritet och skyddar dina personuppgifter i enlighet med EU:s dataskyddsförordning (GDPR). Denna policy beskriver hur vi samlar in, använder och skyddar dina uppgifter."
      },
      {
        heading: "2. Vilka uppgifter vi samlar in",
        text: "Vi samlar in följande personuppgifter:\n- Namn, e-postadress och telefonnummer (vid bokning och kontaktformulär)\n- Elevens ålder och erfarenhetsnivå (vid bokning)\n- Bokningsinformation via Calendly\n- Teknisk data som IP-adress och webbläsartyp (automatiskt)"
      },
      {
        heading: "3. Hur vi använder dina uppgifter",
        text: "Dina uppgifter används för att:\n- Kontakta dig angående lektioner och bokningar\n- Anpassa undervisningen efter dina behov\n- Förbättra vår webbplats och tjänster\n- Skicka relevant information om våra kurser"
      },
      {
        heading: "4. Laglig grund",
        text: "Vi behandlar dina personuppgifter baserat på ditt samtycke (när du fyller i formulär) och för att fullgöra avtal (när du bokar lektioner)."
      },
      {
        heading: "5. Tredjepartstjänster",
        text: "Vi använder följande tredjepartstjänster:\n- Calendly för bokning av lektioner\n- Microsoft Teams, Zoom och Google Meet för videolektioner\n\nDessa tjänster har egna integritetspolicyer som vi rekommenderar att du läser."
      },
      {
        heading: "6. Lagring och säkerhet",
        text: "Dina uppgifter lagras säkert och vi vidtar lämpliga tekniska och organisatoriska åtgärder för att skydda dem. Vi behåller dina uppgifter så länge det är nödvändigt för att tillhandahålla våra tjänster."
      },
      {
        heading: "7. Dina rättigheter",
        text: "Enligt GDPR har du rätt att:\n- Begära tillgång till dina personuppgifter\n- Begära rättelse av felaktiga uppgifter\n- Begära radering av dina uppgifter\n- Invända mot behandling av dina uppgifter\n- Begära dataportabilitet"
      },
      {
        heading: "8. Kontakt",
        text: "Om du har frågor om vår integritetspolicy eller vill utöva dina rättigheter, kontakta oss:\n\nE-post: info@quranonline.se\nTelefon: +46 73 387 44 47"
      }
    ]
  },
  en: {
    title: "Privacy Policy",
    lastUpdated: "Last updated: March 2026",
    sections: [
      {
        heading: "1. Introduction",
        text: "Quran Online (quranonline.se) is operated by Ustadh Ismail. We respect your privacy and protect your personal data in accordance with the EU General Data Protection Regulation (GDPR). This policy describes how we collect, use and protect your data."
      },
      {
        heading: "2. Data We Collect",
        text: "We collect the following personal data:\n- Name, email address and phone number (via booking and contact forms)\n- Student age and experience level (via booking)\n- Booking information via Calendly\n- Technical data such as IP address and browser type (automatically)"
      },
      {
        heading: "3. How We Use Your Data",
        text: "Your data is used to:\n- Contact you regarding lessons and bookings\n- Customize teaching to your needs\n- Improve our website and services\n- Send relevant information about our courses"
      },
      {
        heading: "4. Legal Basis",
        text: "We process your personal data based on your consent (when you fill in forms) and to fulfill contracts (when you book lessons)."
      },
      {
        heading: "5. Third-Party Services",
        text: "We use the following third-party services:\n- Calendly for booking lessons\n- Microsoft Teams, Zoom and Google Meet for video lessons\n\nThese services have their own privacy policies which we recommend you read."
      },
      {
        heading: "6. Storage and Security",
        text: "Your data is stored securely and we take appropriate technical and organizational measures to protect it. We retain your data for as long as necessary to provide our services."
      },
      {
        heading: "7. Your Rights",
        text: "Under the GDPR, you have the right to:\n- Request access to your personal data\n- Request correction of inaccurate data\n- Request deletion of your data\n- Object to processing of your data\n- Request data portability"
      },
      {
        heading: "8. Contact",
        text: "If you have questions about our privacy policy or wish to exercise your rights, contact us:\n\nEmail: info@quranonline.se\nPhone: +46 73 387 44 47"
      }
    ]
  }
};

const PrivacyPage = () => {
  const { language } = useLanguage();
  const t = content[language] || content.sv;

  return (
    <div className="min-h-screen pt-20" style={{ backgroundColor: 'var(--bg-primary)' }} data-testid="privacy-page">
      <section className="py-16 islamic-pattern hero-gradient">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
            {t.title}
          </h1>
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>{t.lastUpdated}</p>
        </div>
      </section>

      <div className="gold-divider"></div>

      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
          {t.sections.map((section, i) => (
            <div key={i}>
              <h2 className="text-lg font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                {section.heading}
              </h2>
              <p className="text-sm leading-relaxed whitespace-pre-line" style={{ color: 'var(--text-secondary)' }}>
                {section.text}
              </p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
};

export default PrivacyPage;
